import { postEvent, bufferForBoostFlush } from './transport';
import { resolveCartToken } from './cartToken';
import { canTrack, detectPageType, generateClientRid, getBoostCartToken, getBoostSessionId, getCollectionId, getCustomerId, getTenantId, hasBoostSession, rememberProductClick, uuidv4, } from './utils';
/**
 * Translates GA4-shaped abstract events to Boost's wire format and posts them
 * to the Boost analytics endpoint. The only file that knows about Boost's
 * `tid`/`eid`/`sid`/`u`/`a`/etc. field shorthand — call sites import only
 * from `src/interfaces/analytics/AnalyticsProvider`.
 *
 * Per Boost's dev doc, only PRODUCT user-actions go to the /e endpoint:
 *
 *   abstract method  → u (user action)        → display name
 *   ──────────────────────────────────────────────────────────────────
 *   trackSelectItem  → view_product           → [Boost] Clicked {Product|Search Result|Recommendation}
 *   trackAddToCart   → add_to_cart            → [Boost] Added To Cart
 *     ↳ isPreOrder   → add_to_cart_pre_order  → [Boost] Pre-Order Added To Cart
 *
 *   with `a` (action category, the interaction context):
 *   plp → filter · search-results → search · recommendations → recommend ·
 *   suggestions → suggest · (unknown) → other
 *
 * Search / filter / suggest / list-impression analytics are NOT /e events —
 * Boost captures them server-side from the search API request itself, which is
 * why `fetchBoost` carries `sid`/`event_type`/`pg`/`customer_id` and the
 * corresponding track methods below are deliberate no-ops.
 *
 * Every sending method short-circuits when `canTrack()` returns false (consent
 * denied) and when `getTenantId()` is empty (storefront not yet initialised).
 */
export class BoostAnalyticsProvider {
    /** Deliberate no-op — the collection/results impression is captured
     *  server-side from the search API request (see `trackSearch`). */
    trackViewItemList(_event) { }
    trackSelectItem(event) {
        var _a, _b;
        // Remember the server rid for this product so a later add-to-cart can
        // attribute to the query that surfaced it — including an ATC fired from the
        // theme PDP after navigation, where the response is gone. Only the server
        // correlationId is worth storing; a client-fallback rid can't be correlated.
        if (event.correlationId) {
            rememberProductClick(String(event.itemId), event.correlationId, this.actionFor(event.itemListId));
        }
        // A product click immediately navigates to the PDP — defer delivery to
        // Boost's buffer so its bundle re-sends on the next page (survives the
        // navigation without keepalive). Falls back to keepalive when Boost isn't
        // present. See `send`'s `defer` handling.
        this.send({
            u: 'view_product',
            a: this.actionFor(event.itemListId),
            pid: String(event.itemId),
            vid: event.itemVariantId,
            qs: this.queryString(event.searchTerm),
            pg: (_a = event.pageType) !== null && _a !== void 0 ? _a : detectPageType(),
            rid: (_b = event.correlationId) !== null && _b !== void 0 ? _b : generateClientRid(),
            wid: event.widgetId,
        }, { defer: true });
    }
    trackAddToCart(event) {
        // Gate the network work (cart-token polling + `cart:updated` listener)
        // behind consent — `send()` re-checks, but `resolveCartToken()` would
        // otherwise hit `/cart.js` before the consent gate is ever consulted.
        if (!canTrack())
            return;
        // `cartToken` may be supplied by the caller (passed in from
        // `shopifyThemeCart.ts` after its own `resolveCartToken`) — in which case
        // we send synchronously. Otherwise resolve here and send async; the
        // event is delayed up to the cart-token timeout (7.5s).
        if (event.cartToken) {
            this.sendAddToCart(event, event.cartToken);
            return;
        }
        resolveCartToken().then(token => this.sendAddToCart(event, token !== null && token !== void 0 ? token : undefined));
    }
    /**
     * Deliberate no-op. Per Boost's dev doc ("How We Pull Data for Analytics"),
     * search/filter/suggest analytics are captured SERVER-SIDE from the search
     * API request itself — `fetchBoost` carries `sid`/`event_type`/`pg`/
     * `customer_id` for exactly this. The /e endpoint only accepts the four
     * product user-actions (view_product, quick_view, add_to_cart, buy_now);
     * standalone u-less search/filter posts are off-protocol. The method stays
     * so call sites remain provider-agnostic (a future Algolia provider would
     * implement it for real).
     */
    trackSearch(_event) { }
    /** Deliberate no-op — see `trackSearch`. The applied filter params travel on
     *  the filter API request, which Boost captures server-side. */
    trackSelectFilter(_event) { }
    /**
     * Build a product event's `qs` matching Boost's bundle convention: scoped to
     * the search query (`q=<term>`) on a search context or to the collection
     * (`collection_scope=<id>`) on a PLP. A bare term without the `q=` key
     * wouldn't parse as the query on Boost's side. Returns `undefined` when
     * there's nothing to send so the field is stripped from the wire.
     */
    queryString(searchTerm) {
        if (searchTerm)
            return `q=${encodeURIComponent(searchTerm)}`;
        const collectionId = getCollectionId();
        return collectionId ? `collection_scope=${collectionId}` : undefined;
    }
    /** Deliberate no-op — see `trackSearch`. `fetchBoostSearchSuggest` carries
     *  `event_type=suggest` + `sid`, which is Boost's suggest capture. */
    trackSearchSuggested(_event) { }
    sendAddToCart(event, cartToken) {
        var _a, _b, _c;
        this.send({
            u: event.isPreOrder ? 'add_to_cart_pre_order' : 'add_to_cart',
            a: this.actionFor(event.itemListId),
            pid: String(event.itemId),
            vid: event.itemVariantId,
            qs: this.queryString(event.searchTerm),
            pg: (_a = event.pageType) !== null && _a !== void 0 ? _a : detectPageType(),
            rid: (_b = event.correlationId) !== null && _b !== void 0 ? _b : generateClientRid(),
            wid: event.widgetId,
            ct: cartToken,
            bct: getBoostCartToken(),
            bpids: ((_c = event.bundleItemIds) === null || _c === void 0 ? void 0 : _c.length) ? event.bundleItemIds.join(',') : undefined,
        });
    }
    /**
     * Compose the wire payload and post. Adds the boilerplate every event
     * shares (`tid`, `eid`, `sid`, `t`, `cid`, `r`) and drops `undefined`
     * fields so the JSON stays compact.
     */
    send(fields, opts = {}) {
        if (!canTrack())
            return;
        const tid = getTenantId();
        if (!tid)
            return;
        const payload = Object.assign({ tid, eid: uuidv4(), sid: getBoostSessionId(), t: new Date().toISOString(), cid: getCustomerId(), r: typeof document !== 'undefined' ? document.referrer : undefined }, fields);
        // Strip `undefined` so the wire stays compact and matches Boost's bundle
        // behaviour (it never sends a field as `null`/`undefined`).
        for (const k of Object.keys(payload)) {
            if (payload[k] === undefined)
                delete payload[k];
        }
        // Deferred delivery (navigation-racing clicks): hand the event to Boost's
        // buffer so its bundle re-sends it on the next page. Only when Boost's
        // session is present to flush it; otherwise fall back to a keepalive POST.
        if (opts.defer && hasBoostSession() && bufferForBoostFlush(payload))
            return;
        postEvent(payload);
    }
    actionFor(itemListId) {
        switch (itemListId) {
            // Boost's documented ACTION enum is filter|search|suggest|recommend —
            // there is no `collection`. A PLP product interaction is a FILTER-context
            // event per Boost's own reference implementation (its V2 code sends
            // `Utils.isSearchPage() ? SEARCH : FILTER` for grid clicks).
            case 'plp': return 'filter';
            case 'search-results': return 'search';
            case 'recommendations': return 'recommend';
            case 'suggestions': return 'suggest';
            // Custom/unknown surface → `other`, matching the bundle's `a: m||"other"`
            // default rather than mislabelling it as a filter action.
            default: return 'other';
        }
    }
}
